// ThemeContext.tsx

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { useLocalStorage } from 'usehooks-ts';

type ThemeType = 'light' | 'dark';
type ThemeContextType = {
  mode: ThemeType;
  toggleTheme: () => void;
};

// Provide a default that matches the type (won't be used in real app)
const ThemeModeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

// Mirrors the brand tokens defined in src/scss/_global.scss and
// src/scss/abstracts/_variables.scss, so MUI components (Button, Card,
// Dialog, Alert, DataGrid, ...) match the rest of the app instead of
// falling back to MUI's default blue palette.
const palettesByMode = {
  light: {
    primary: { main: '#ee384e' },
    secondary: { main: '#3c4b6d' },
    // background.paper deliberately left at MUI's default (white): the
    // Sass zebra-striping on table rows (`tr:nth-child(even)`) uses
    // --card-bgc (#f9f9fa), which is meant to read as a step *away* from
    // the Card's base background. MUI's <Card>/<Paper> set their own
    // background-color directly on the root element (always wins,
    // regardless of cascade order), so setting paper to that same
    // #f9f9fa made the Card background and the "even row" stripe
    // identical — the zebra striping visually disappeared.
    background: { default: '#f2f2f2' },
    // Deliberately NOT overriding text.primary here: MUI's default
    // light-mode text (rgba(0,0,0,0.87)) has much higher contrast than the
    // brand's --text-color (#424750). MUI components like Card/Paper set
    // `color` directly on their root (higher specificity than the global
    // `body` rule), so overriding this dimmed text in tables/dialogs
    // noticeably compared to the rest of the (Sass-styled) app.
    success: { main: '#246500' },
    info: { main: '#086785' },
    error: { main: '#9d0e0e' },
  },
  dark: {
    primary: { main: '#ee384e' },
    secondary: { main: '#3c4b6d' },
    // background.paper left at MUI's default for the same reason as light
    // mode above — avoid colliding with the Sass zebra-stripe color.
    background: { default: '#283036' },
    text: { primary: '#ebebeb' },
    success: { main: '#d0ffb6' },
    info: { main: '#a5e9ff' },
    error: { main: '#f2bebe' },
  },
} as const;

export const CustomThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeType>('light');
  const [localTheme] = useLocalStorage('theme', 'light');

  useEffect(() => {
    if (localTheme) {
      setMode(localTheme as ThemeType);
    }
  }, [localTheme]);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...palettesByMode[mode],
        },
        typography: {
          // Not overriding fontFamily: MUI's CssBaseline sets `font-family`
          // on <body> at the same specificity as the Sass rule that also
          // targets `body`, so whichever one wins here applies app-wide.
          // Forcing Poppins here made it win (it hadn't been rendering
          // before), and it reads as less legible than MUI's own default
          // (Roboto) in dense tables. Leave MUI's default in place.
          h1: { fontSize: '3.5rem' }, // 57px - fontSizeHeadlineLarge
          h2: { fontSize: '2.8125rem' }, // 45px - fontSizeHeadlineMedium
          h3: { fontSize: '1.5rem' }, // 24px - fontSizeHeadlineSmall
          body1: { fontSize: '1rem' }, // 16px - fontSizeBodyLarge
          body2: { fontSize: '0.875rem' }, // 14px - fontSizeBodyMedium
          caption: { fontSize: '0.75rem' }, // 12px - fontSizeBodySmall
        },
        shape: {
          borderRadius: 8, // smallBorderRadius, used as MUI's default component radius
        },
        components: {
          MuiCard: {
            styleOverrides: {
              root: { borderRadius: 12 }, // mediumBorderRadius
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: { borderRadius: 12 },
            },
          },
        },
      }),
    [mode],
  );

  return (
    <ThemeModeContext.Provider value={{ toggleTheme, mode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
